import React from 'react';
import { $_ } from '../i18n';

interface RevealEmailButtonProps {
  emailCharCodes: number[];
  buttonLabel: string;
  ariaLabel?: string;
}

type CopyStatus = 'idle' | 'copied' | 'error';

const RevealEmailButton: React.FC<RevealEmailButtonProps> = ({
  emailCharCodes,
  buttonLabel,
  ariaLabel
}) => {
  const [emailAddress, setEmailAddress] = React.useState<string | null>(null);
  const [copyStatus, setCopyStatus] = React.useState<CopyStatus>('idle');
  const [isStatusVisible, setIsStatusVisible] = React.useState(false);
  const [copyEventId, setCopyEventId] = React.useState(0);

  const copyUsingExecCommand = React.useCallback((value: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = value;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '1px';
    textArea.style.height = '1px';
    textArea.style.opacity = '0';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textArea);
    }
  }, []);

  const handleCopyClick = React.useCallback(async () => {
    const decodedEmail = String.fromCharCode(...emailCharCodes);

    setEmailAddress(decodedEmail);
    setCopyStatus('idle');
    setIsStatusVisible(false);
    setCopyEventId(Date.now());

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(decodedEmail);
      } else {
        copyUsingExecCommand(decodedEmail);
      }

      setCopyStatus('copied');
    } catch (error) {
      console.error('Failed to copy email to clipboard', error);
      setCopyStatus('error');
    }
  }, [copyUsingExecCommand, emailCharCodes]);

  React.useEffect(() => {
    if (copyStatus === 'idle' || !emailAddress) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      setIsStatusVisible(true);
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [copyStatus, emailAddress, copyEventId]);

  const statusDetails = React.useMemo(() => {
    if (!emailAddress || copyStatus === 'idle') {
      return null;
    }

    if (copyStatus === 'copied') {
      return {
        tone: 'success' as const,
        title: $_('page.shared.revealEmail.copySuccessTitle'),
        info: $_('page.shared.revealEmail.copySuccessInfo')
      };
    }

    return {
      tone: 'error' as const,
      title: $_('page.shared.revealEmail.copyErrorTitle'),
      info: $_('page.shared.revealEmail.copyErrorInfo')
    };
  }, [copyStatus, emailAddress]);

  const buttonText =
    copyStatus === 'copied'
      ? $_('page.shared.revealEmail.copyAgainButton')
      : buttonLabel;

  const toneClasses =
    statusDetails?.tone === 'error'
      ? 'border-red-200 bg-red-50 text-red-900 ring-red-100'
      : 'border-emerald-200 bg-emerald-50 text-emerald-900 ring-emerald-100';

  const emailHighlightClasses =
    statusDetails?.tone === 'error'
      ? 'text-red-700 ring-red-200'
      : 'text-emerald-700 ring-emerald-200';

  return (
    <div className="not-prose mt-4 space-y-3">
      <button
        type="button"
        className="inline-flex items-center rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        onClick={handleCopyClick}
        aria-label={ariaLabel ?? buttonLabel}
      >
        {buttonText}
      </button>

      {statusDetails && (
        <div
          key={`${copyStatus}-${emailAddress}`}
          className={`origin-top rounded-lg border px-4 py-3 text-sm shadow-sm ring-1 transition duration-300 ease-out ${
            isStatusVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
          } ${toneClasses}`}
          aria-live="polite"
        >
          <p className="text-sm font-semibold tracking-tight">
            {statusDetails.title}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span
              className={`rounded-md bg-white/80 px-2 py-1 font-mono text-xs font-medium tracking-tight shadow-sm ring-1 ${emailHighlightClasses}`}
            >
              {emailAddress}
            </span>

            <span>{statusDetails.info}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevealEmailButton;
